/**
 * Irrigation Program API Service
 *
 * Provides methods for interacting with irrigation program
 * backend endpoints including programs, steps, and status management.
 */

import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/** One sector a step irrigates, resolved server-side. */
export interface ProgramStepSectorInfo {
  sector_id: number;
  sector_name: string | null;
  /** Order within a sector-backed step; the Opta sequence group order. */
  position: number | null;
}

export interface ProgramStep {
  id: number;
  program_id: number;
  step_order: number;
  /** NULL on a sector-backed step: it has no zone. */
  zone_id: number | null;
  zone_name: string | null;
  /** Which mode this step is in, so the editor need not infer it from zone_id. */
  target: 'zone' | 'sectors';
  /** The sectors this step will irrigate — the zone's current members, or the
   *  explicit list. Resolved server-side so a row renders without a request each. */
  sectors: ProgramStepSectorInfo[];
  duration_minutes: number;
  delay_after_minutes: number;
  planned_water_mm: number | null;
  max_water_mm: number | null;
  has_fertigation: boolean;
  /**
   * Q (m3/h) this step demands while it runs: its zone's sectors open TOGETHER,
   * so their design flows add. `null` = no sector could be sized (never 0).
   * `flow_partial` marks the sum as a FLOOR because some sector is missing its
   * emitter specs.
   */
  flow_m3h?: number | null;
  flow_partial?: boolean;
  /**
   * The recipe this step runs — the writable part of its fertigation. The mix
   * itself lives in the farm's library (`fertigationRecipeApi`) and is shared by
   * every step that picks it, so editing it there reaches all of them.
   */
  recipe_id: number | null;
  recipe_name: string | null;
  /** Resolved from that recipe, read-only. Write through `recipe_id`, or by
   *  editing the recipe itself in the farm's library. */
  fert_doses: FertDose[];
  fert_stabilise_s: number;
  fert_clean_s: number;
  fert_align: 'start' | 'end';
  created_at: string;
  updated_at: string;
}

/**
 * One dosing a STEP runs: which pump draws from which drum, and how much.
 *
 * List order is the injection order. `parallel` is honoured only between
 * DIFFERENT pumps — a pump draws from one drum at a time, so dosings sharing a
 * pump always run in series.
 */
export interface FertDose {
  /**
   * The injection pump. NULL on a SIMPLE head, which has no pump rows at all —
   * one fertilizer relay switched by the server — so the entry is just a time.
   */
  injector_id: number | null;
  /** Required whenever the pump is plumbed to more than one drum. */
  tank_id: number | null;
  /** Dose by time, or the SAFETY TIMEOUT when dosing by litres. */
  inject_s: number;
  /** Dose by litres through the drum's meter (needs a drum with a counter). */
  inject_l: number | null;
  parallel?: boolean;
}

export type ExecutionStrategy = 'on_device' | 'server_side' | 'hybrid';

export type AdjustmentMode = 'manual' | 'suggest' | 'auto';

export interface Program {
  id: number;
  farm_id: string;
  name: string;
  description: string | null;
  execution_mode: 'sequential' | 'parallel';
  execution_strategy: ExecutionStrategy;
  adjustment_mode: AdjustmentMode;
  /**
   * Which pump station this program starts. `null` = derive it from the sectors,
   * which is the historical behaviour and still the default. Set it on farms
   * where several stations can feed the same sectors — e.g. a solar head by day
   * and a generator-backed head at night.
   */
  pump_station_id: number | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  is_enabled: boolean;
  recurrence_type: 'daily' | 'weekly' | 'interval' | 'one_time';
  recurrence_days: string[] | null;
  recurrence_interval_days: number | null;
  start_time: string;
  start_date: string | null;
  end_date: string | null;
  zone_cycling_enabled: boolean;
  current_cycle_index: number | null;
  rain_skip_enabled: boolean;
  rain_skip_threshold_mm: number | null;
  last_executed_at: string | null;
  legacy_schedule_id: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  steps?: ProgramStep[];
  warnings?: string[];
}

export interface ProgramSummary {
  id: number;
  farm_id: string;
  name: string;
  execution_mode: 'sequential' | 'parallel';
  execution_strategy: ExecutionStrategy;
  adjustment_mode: AdjustmentMode;
  pump_station_id: number | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  is_enabled: boolean;
  recurrence_type: string;
  recurrence_days: string[] | null;
  start_time: string;
  start_date: string | null;
  end_date: string | null;
  zone_cycling_enabled: boolean;
  step_count: number;
  total_duration_minutes: number;
  /**
   * Peak Q: the most this program ever asks of its head AT ONCE — the largest
   * step when sequential, the SUM of them when parallel (everything opens
   * together). `peak_flow_partial` marks it as a FLOOR because some sector could
   * not be sized; under-reporting demand is what makes an oversubscribed head
   * look fine.
   *
   * `exceeds_pump` is only true/false when BOTH numbers are real. `null` means
   * "nothing to compare" and must never be rendered as a pass.
   */
  peak_flow_m3h?: number | null;
  peak_flow_partial?: boolean;
  pump_capacity_m3h?: number | null;
  exceeds_pump?: boolean | null;
  sync_status?: string;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProgramListResponse {
  items: ProgramSummary[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ProgramCreateRequest {
  farm_id: string;
  name: string;
  description?: string;
  execution_mode?: 'sequential' | 'parallel';
  execution_strategy?: ExecutionStrategy;
  adjustment_mode?: AdjustmentMode;
  /** null / omitted = derive the station from the sectors. */
  pump_station_id?: number | null;
  recurrence_type?: 'daily' | 'weekly' | 'interval' | 'one_time';
  recurrence_days?: string[];
  recurrence_interval_days?: number;
  start_time: string;
  start_date?: string;
  end_date?: string;
  zone_cycling_enabled?: boolean;
  rain_skip_enabled?: boolean;
  rain_skip_threshold_mm?: number;
  steps?: StepCreateRequest[];
}

export interface ProgramUpdateRequest {
  name?: string;
  description?: string;
  execution_mode?: 'sequential' | 'parallel';
  execution_strategy?: ExecutionStrategy;
  adjustment_mode?: AdjustmentMode;
  /**
   * Send `null` EXPLICITLY to clear the override back to sector-derived: the
   * backend applies `exclude_unset`, so an omitted key leaves the current value
   * alone while an explicit null really clears it.
   */
  pump_station_id?: number | null;
  recurrence_type?: string;
  recurrence_days?: string[];
  recurrence_interval_days?: number;
  start_time?: string;
  start_date?: string;
  end_date?: string;
  zone_cycling_enabled?: boolean;
  rain_skip_enabled?: boolean;
  rain_skip_threshold_mm?: number;
  is_enabled?: boolean;
}

export interface StepCreateRequest {
  /** Give EITHER zone_id (expands to the zone's members at run time) OR sector_ids
   *  (exactly these, in this order). The API rejects both or neither. */
  zone_id?: number;
  sector_ids?: number[];
  step_order: number;
  duration_minutes: number;
  delay_after_minutes?: number;
  planned_water_mm?: number;
  max_water_mm?: number;
  has_fertigation?: boolean;
  /** Which recipe this step runs. The mix lives in the farm's library. */
  recipe_id?: number | null;
}

export interface StepUpdateRequest {
  /** Sending one retargets the step and clears the other. Sending neither leaves the
   *  targeting untouched, which is how a duration-only edit works. */
  zone_id?: number;
  sector_ids?: number[];
  duration_minutes?: number;
  delay_after_minutes?: number;
  planned_water_mm?: number | null;
  max_water_mm?: number | null;
  has_fertigation?: boolean;
  /** Which recipe this step runs. The mix lives in the farm's library. */
  recipe_id?: number | null;
}

/**
 * What a zone's head can do about fertigation — the form follows the hardware.
 *
 * `none`: no fertilizer output. `simple`: one relay on a LoRaWAN head, so a dose
 * is a TIME and the server runs it step by step. `station`: an Opta cabezal with
 * injection pumps, drums and meters, so a dose can also be LITRES.
 *
 * `per_step_on_device` is about the CONTROLLER, not the tier: firmware that
 * reports `caps.recetas` reads a recipe per step, older firmware replays one
 * fertigation over the whole program and only the first fertigated step reaches
 * it. It flips on its own when a controller is upgraded.
 */
export interface ZoneFertOptions {
  tier: 'none' | 'simple' | 'station';
  station_id: number | null;
  station_name: string | null;
  pumps: { id: number; name: string; enabled: boolean; tank_ids: number[] }[];
  tanks: { id: number; name: string; enabled: boolean; has_meter: boolean }[];
  /**
   * The agronomic TARGET for this zone (L/ha for the crop at its stage), resolved
   * from species+stage ▸ zone ▸ sector. It orients which recipe to use and drives
   * the injection-time preview; it is NOT a recipe and nothing executes it while
   * the step has one.
   */
  recipe: {
    label: string | null;
    inject_s: number;
    stabilise_s: number;
    clean_s: number;
    align: 'start' | 'end';
    dose_lha: number | null;
    dose_mode: string;
  } | null;
  /** False while the controller cannot take a different dose per step. */
  per_step_on_device: boolean;
}

export interface StatusUpdateRequest {
  status: 'active' | 'paused' | 'completed' | 'archived';
  reason?: string;
}

export interface ProgramExecutionEvent {
  date: string;
  start_time: string;          // scheduled HH:MM
  end_time: string;            // scheduled HH:MM + duration
  total_duration_minutes: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'unconfirmed' | 'partial' | 'skipped' | 'error';
  progress: number;
  execution_id: number | null;
  error_message?: string | null;
  // Populated only when a run exists. For manual off-schedule runs these
  // differ from start_time/end_time — frontend renders both.
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  trigger_type?: 'manual' | 'scheduled' | null;
}

export interface ProgramDashboardStep {
  zone_name: string;
  duration_minutes: number;
  delay_after_minutes: number;
  has_fertigation?: boolean;
  fertigation?: {
    inject_at: 'start' | 'end';
    stabilize_min: number;
    inject_min: number;
    wash_min: number;
  } | null;
}

export interface ProgramDashboardItem {
  program_id: number;
  program_name: string;
  sector_names: string[];
  execution_mode: string;
  has_fertigation: boolean;
  steps: ProgramDashboardStep[];
  events: ProgramExecutionEvent[];
}

export interface DashboardProgramSummaryResponse {
  items: ProgramDashboardItem[];
}

export interface ExecutionConfirmRequest {
  execution_date: string;
  execution_status: 'completed' | 'partial' | 'skipped' | 'error';
  actual_duration_minutes?: number;
  notes?: string;
}

export interface ProgramExecutionResponse {
  id: number;
  program_id: number;
  execution_date: string;
  execution_status: string;
  actual_duration_minutes: number | null;
  notes: string | null;
  confirmed_by_user_id: number | null;
  confirmed_at: string;
}

// ============================================================================
// Execution Run Types
// ============================================================================

export type RunStatus = 'pending' | 'starting' | 'running' | 'completing' | 'completed' | 'partial' | 'error' | 'aborted';
export type StepExecStatus = 'pending' | 'sending_commands' | 'irrigating' | 'delay' | 'completed' | 'error' | 'skipped';
/** What to SHOW. `queued` = armed for a later time, not watering yet. */
export type StepDisplayStatus = StepExecStatus | 'queued';

export interface StepExecution {
  id: number;
  step_id: number | null;
  step_order: number;
  zone_id: number | null;
  zone_name: string | null;
  status: StepExecStatus;
  /** Backend's display verdict; falls back to `status` when absent. */
  display_status?: StepDisplayStatus | null;
  started_at: string | null;
  completed_at: string | null;
  planned_duration_sec: number | null;
  actual_duration_sec: number | null;
  valves_opened: Array<{
    device_id: number;
    dev_eui: string;
    valve_number: number;
    valve_id: string;
    sector_id: number;
    sector_name?: string;
  }> | null;
  valves_failed: Array<{
    device_id: number;
    dev_eui: string;
    valve_number: number;
    error: string;
  }> | null;
  error_message: string | null;
}

export interface ExecutionRun {
  id: number;
  program_id: number;
  program_name: string | null;
  execution_date: string;
  run_status: RunStatus;
  execution_mode: string | null;
  current_step_index: number | null;
  total_steps: number | null;
  started_at: string | null;
  completed_at: string | null;
  aborted_at: string | null;
  error_message: string | null;
  // 'device' is set by the backend when a CONTROLLER announces it started the
  // program (opta_ingest_service._track_run_on_program_state) — the run exists so
  // the operator can see the irrigation, but the controller owns the timeline and
  // there are NO step_executions. The union omitted it, so such a run rendered as
  // 'scheduled' and its empty step list read as "still loading" forever.
  trigger_type: 'scheduled' | 'manual' | 'device';
  triggered_by_user_id: number | null;
  farm_id: string;
  metadata: Record<string, unknown> | null;
  /** What the API actually sends: the schema's `metadata` field is aliased. */
  run_metadata?: Record<string, unknown> | null;
  created_at: string;
  step_executions?: StepExecution[];
  // Live-only fields pushed via the WS progress payload (not persisted).
  fert_phase?: 'stabilizing' | 'injecting' | 'washing' | null;
  last_message?: string | null;
}

export interface ExecutionRunListResponse {
  runs: ExecutionRun[];
  total: number;
}

export interface ActiveExecutionsResponse {
  executions: ExecutionRun[];
}

export interface RuleSyncResponse {
  message: string;
  program_id: number;
  // Legacy fields, kept for backward compat; may be absent on 202 response:
  rules_synced?: number;
  devices?: string[];
}

export interface RuleSlotInfo {
  id: number;
  device_id: number;
  rule_slot_id: number;
  valve_number: number;
  program_step_id: number | null;
  sync_status: string;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface RuleSlotsResponse {
  program_id: number;
  total_mappings: number;
  slots: RuleSlotInfo[];
}

export type SyncOverallStatus =
  | 'synced'
  | 'syncing'
  | 'pending'
  | 'error'
  | 'no_rules'
  | 'not_applicable'
  // Added 2026-08-13. The first four came from the stored sync_status string,
  // which could only report on the last publish. These three come from actually
  // comparing the device's echoed rule against the program's live schedule.
  | 'drifted'            // the device holds a rule, and it is the wrong one
  | 'stale_evidence'     // the device has not spoken about this slot in days
  | 'no_device_report';  // the device has never reported this slot

/**
 * Per-valve verdict from /sync-status. Optional so the UI still renders against
 * a backend that predates the content comparison.
 */
export type SyncMappingVerdict =
  | 'armed'
  | 'drifted'
  | 'stale_evidence'
  | 'no_device_report'
  | 'unconfirmed'
  // The program has disowned this valve — a replaced controller, a re-sync that
  // vacated the slot. The row is kept only to hold the slot for a re-arm, so it
  // is not a fault and not part of "is this program armed?".
  | 'retired'
  // ...and the dangerous half: disowned, a disarm was sent, and the device never
  // acknowledged it. Its rule may still be armed on hardware nobody owns.
  | 'retired_unconfirmed';

export interface SyncStatusMapping {
  id: number;
  device_id: number;
  /**
   * Set when this device is owed a rule update that has NOT been sent, because
   * its valve is watering and any rule write cancels a UC51x's countdown. Not an
   * error and not `syncing`: nothing was published and nothing is wrong.
   */
  hold?: RuleSyncHold | null;
  device_name: string | null;
  rule_slot_id: number;
  valve_number: number;
  valve_name: string | null;
  sector_name: string | null;
  program_step_id: number | null;
  repeat_weekdays: number | null;
  /** Days the platform INTENDED to arm (decoded from the mapping's mask). */
  days: string[];
  time_duration_seconds: number | null;
  sync_status: string;
  last_synced_at: string | null;
  sync_error: string | null;
  sync_attempts: number;
  // --- the comparison, added 2026-08-13 -----------------------------------
  /** Verdict over three questions: confirmed, recent, and content-correct. */
  verdict?: SyncMappingVerdict;
  /** Human-readable divergences. Empty when `verdict === 'armed'`. */
  mismatches?: string[];
  /** Days the DEVICE echoed. Null when it has never reported this slot. */
  device_days?: string[] | null;
  is_confirmed?: boolean;
  last_published_at?: string | null;
  /** Seconds since the device last said anything about this slot. */
  evidence_age_s?: number | null;
}

/** A rule update a device is owed but has not been sent. */
export interface RuleSyncHold {
  release_after: string | null;
  blocking_program_id: number | null;
  blocking_step_order: number | null;
  reason: string;
  attempts: number;
}

export interface ProgramSyncStatus {
  program_id: number;
  /** Devices whose rule update is waiting for their valve to finish watering. */
  held_devices?: number[];
  execution_strategy: 'on_device' | 'server_side' | 'hybrid';
  overall_status: SyncOverallStatus;
  total_mappings: number;
  counts: Partial<Record<string, number>>;
  /** Counts by `verdict` rather than by the stored sync_status string. */
  verdict_counts?: Partial<Record<SyncMappingVerdict, number>>;
  /** The program's recurrence days as defined right now. */
  program_days?: string[];
  program_start_time?: string | null;
  /** Hours after which a device confirmation stops counting as evidence. */
  evidence_max_age_hours?: number | null;
  oldest_last_synced_at: string | null;
  newest_last_synced_at: string | null;
  mappings: SyncStatusMapping[];
}

export interface ExecutionProgressMessage {
  type: 'program_execution';
  run_id: number;
  program_id: number;
  farm_id: string;
  status: RunStatus;
  current_step: number | null;
  total_steps: number | null;
  execution_mode: string | null;
  message: string;
  timestamp: string;
}

// ============================================================================
// Helper
// ============================================================================

const buildQueryString = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

// ============================================================================
// Program API
// ============================================================================

export const listPrograms = async (
  farmId: string,
  params?: { skip?: number; limit?: number; status?: string; zone_id?: number; has_fertigation?: boolean }
): Promise<ProgramListResponse> => {
  const qs = params ? buildQueryString(params) : '';
  return apiClient.get<ProgramListResponse>(`/irrigation-programs/farm/${farmId}${qs}`);
};

export const getProgram = async (programId: number): Promise<Program> => {
  return apiClient.get<Program>(`/irrigation-programs/${programId}`);
};

export const createProgram = async (data: ProgramCreateRequest): Promise<Program> => {
  return apiClient.post<Program>('/irrigation-programs', data);
};

export const updateProgram = async (programId: number, data: ProgramUpdateRequest): Promise<Program> => {
  return apiClient.put<Program>(`/irrigation-programs/${programId}`, data);
};

export const deleteProgram = async (programId: number): Promise<void> => {
  return apiClient.delete(`/irrigation-programs/${programId}`);
};

export const updateProgramStatus = async (programId: number, data: StatusUpdateRequest): Promise<Program> => {
  return apiClient.patch<Program>(`/irrigation-programs/${programId}/status`, data);
};

// ============================================================================
// Estimation API
// ============================================================================

export interface WaterEstimateResponse {
  zone_id: number;
  duration_minutes: number;
  estimated_mm: number | null;
}

export const estimateWater = async (
  zoneId: number,
  durationMinutes: number
): Promise<WaterEstimateResponse> => {
  return apiClient.get<WaterEstimateResponse>(
    `/irrigation-programs/estimate-water?zone_id=${zoneId}&duration_minutes=${durationMinutes}`
  );
};

/**
 * Depth for an explicit sector set — a sector-backed step has no zone to estimate
 * from, so without this the editor would show a blank until the step was saved.
 */
export const estimateWaterForSectors = async (
  sectorIds: number[],
  durationMinutes: number
): Promise<WaterEstimateResponse> => {
  return apiClient.get<WaterEstimateResponse>(
    `/irrigation-programs/estimate-water?sector_ids=${sectorIds.join(',')}`
    + `&duration_minutes=${durationMinutes}`
  );
};

/**
 * Fertigation options per zone, so the step editor can follow the hardware
 * instead of offering fields the head cannot honour.
 */
/**
 * @param stationId The head the PROGRAM has chosen. It outranks the one a zone's
 *   sectors imply — with two heads able to feed the same sectors, which one doses
 *   depends on the program, not the sector. Omitting it makes a zone whose sectors
 *   name no station report `tier: 'none'` ("this head has no fertilizer output")
 *   even when the program's head has an injection pump.
 */
/** Fertigation options keyed by zone id, and by sector id for steps that name
 *  their sectors directly (they have no zone to look up). */
export interface FertOptionsByTarget {
  zones: Record<string, ZoneFertOptions>;
  sectors: Record<string, ZoneFertOptions>;
}

export const fetchFertOptions = async (
  farmId: string,
  stationId?: number | null,
): Promise<FertOptionsByTarget> => {
  const qs = stationId != null ? `?station_id=${encodeURIComponent(stationId)}` : '';
  const res = await apiClient.get<Partial<FertOptionsByTarget>>(
    `/irrigation-programs/farm/${encodeURIComponent(farmId)}/fert-options${qs}`,
  );
  return { zones: res.zones ?? {}, sectors: res.sectors ?? {} };
};

// ============================================================================
// Step API
// ============================================================================

export const addStep = async (programId: number, data: StepCreateRequest): Promise<ProgramStep> => {
  return apiClient.post<ProgramStep>(`/irrigation-programs/${programId}/steps`, data);
};

export const updateStep = async (stepId: number, data: StepUpdateRequest): Promise<ProgramStep> => {
  return apiClient.put<ProgramStep>(`/irrigation-programs/steps/${stepId}`, data);
};

export const deleteStep = async (stepId: number): Promise<void> => {
  return apiClient.delete(`/irrigation-programs/steps/${stepId}`);
};

export const reorderSteps = async (programId: number, stepIds: number[]): Promise<ProgramStep[]> => {
  return apiClient.put<ProgramStep[]>(`/irrigation-programs/${programId}/reorder`, { step_ids: stepIds });
};

// ============================================================================
// Dashboard Summary
// ============================================================================

export const getDashboardSummary = async (
  farmId: string
): Promise<DashboardProgramSummaryResponse> => {
  return apiClient.get<DashboardProgramSummaryResponse>(
    `/irrigation-programs/farm/${farmId}/dashboard-summary`
  );
};

export const confirmExecution = async (
  programId: number,
  data: ExecutionConfirmRequest
): Promise<ProgramExecutionResponse> => {
  return apiClient.post<ProgramExecutionResponse>(
    `/irrigation-programs/${programId}/executions`,
    data
  );
};

// ============================================================================
// Execution Run API
// ============================================================================

export interface ManualOverlapInfo {
  type: string;
  program_id?: number;
  program_name?: string;
  run_id?: number;
  shared_valve_count?: number;
  scheduled_start?: string;
  scheduled_end?: string;
  message: string;
}

/** One valve in a program-level pre-flight check (PR-UX4).
 *  Same shape as QuickIrrigateResolvedValve so the dialog can render both
 *  without branching. */
export interface PreflightValve {
  dev_eui: string;
  valve_number: number;
  sector_id: number;
  sector_name?: string | null;
  device_id?: number | null;
  device_name?: string | null;
  device_last_seen?: string | null;
  zone_id?: number | null;
  zone_name?: string | null;
  step_order?: number;
  reason?: string;  // 'never_seen' / 'stale_over_threshold' / 'no_device'
}

export interface ManualOverlapError {
  message: string;
  hard_conflicts: ManualOverlapInfo[];
  warnings: ManualOverlapInfo[];
  // PR-UX4 — added when /execute returns 409 due to offline devices.
  // Present alongside (possibly empty) hard_conflicts + warnings so the
  // dialog can render every pre-flight reason in one place.
  offline_valves?: PreflightValve[];
  available_valves?: PreflightValve[];
  stale_valves?: PreflightValve[];
}

/** What starting this program by hand would do, one entry per way of doing it. */
export interface ManualRunPlan {
  ends_at: string | null;
  ends_after_sunset: boolean | null;
  minutes_after_sunset: number;
  available: boolean;
  steps_remaining: number;
  /** How many valve downlinks this costs. `adopt` is always 0. */
  valve_downlinks: number | null;
  unavailable_reason: string | null;
}

export interface ArmedStep {
  step_id: number;
  step_order: number;
  zone_id: number;
  zone_name: string | null;
  duration_minutes: number;
  armed: boolean;
  starts_at: string | null;
  ends_at: string | null;
  state: 'done' | 'irrigating' | 'pending' | 'unarmed';
}

export interface ManualRunPreview {
  program_id: number;
  program_name: string;
  execution_strategy: string | null;
  execution_mode: string | null;
  /** True when a rule the DEVICES are holding covers right now. */
  in_flight: boolean;
  now: string;
  sunset: string | null;
  armed_timeline: ArmedStep[];
  steps_total: number;
  steps_done: number;
  adopt: ManualRunPlan;
  restart: ManualRunPlan;
}

export type ManualRunMode = 'adopt' | 'restart';

export const getManualRunPreview = async (
  programId: number,
): Promise<ManualRunPreview> =>
  apiClient.get<ManualRunPreview>(
    `/irrigation-programs/${programId}/manual-run-preview`,
  );

export const triggerExecution = async (
  programId: number,
  executionDate?: string,
  confirmOverlap = false,
  confirmOfflineSkip = false,
  mode?: ManualRunMode,
): Promise<ExecutionRun> => {
  const params: string[] = [];
  if (confirmOverlap) params.push('confirm_overlap=true');
  if (confirmOfflineSkip) params.push('confirm_offline_skip=true');
  if (mode) params.push(`mode=${mode}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return apiClient.post<ExecutionRun>(
    `/irrigation-programs/${programId}/execute${qs}`,
    executionDate ? { execution_date: executionDate } : {}
  );
};

export interface SkipStepResult {
  run_id: number;
  incoming_step_index: number;
  total_steps: number;
  valves_to_open: number;
  valves_to_close: { device_id: number; valve_number: number }[];
  incoming_duration_s: number;
  commands: string[];
}

/**
 * Skip the step a running program is on.
 *
 * NOT a passthrough to the controller's `advance`. On a head that also drives
 * UC51x valves the backend refuses with 409 and an explanation, because moving
 * the head's step machine alone leaves it pumping against sectors whose valves
 * will not open for hours. Surface that detail verbatim — it tells the operator
 * what to do instead.
 */
export const skipCurrentStep = async (
  programId: number,
): Promise<SkipStepResult> =>
  apiClient.post<SkipStepResult>(`/irrigation-programs/${programId}/skip-step`, {});

export const getRunHistory = async (
  programId: number,
  params?: { start_date?: string; end_date?: string; run_status?: string; limit?: number; offset?: number }
): Promise<ExecutionRunListResponse> => {
  const qs = params ? buildQueryString(params) : '';
  return apiClient.get<ExecutionRunListResponse>(
    `/irrigation-programs/${programId}/runs${qs}`
  );
};

export interface RunValveCommand {
  command_id: string;
  action: 'open' | 'close';
  sequence: number | null;
  current_state: string;
  source: string;
  created_at: string | null;
  sent_at: string | null;
  network_acked_at: string | null;
  acknowledged_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

export interface RunValveState {
  device_id: number;
  dev_eui: string;
  device_name: string | null;
  valve_number: number;
  sector_name: string | null;
  last_command: RunValveCommand | null;
  valve_status: 'open' | 'close' | null;
}

export interface RunPumpOutputState {
  /**
   * 'injector' is an injection pump (a ControllerInjector, not a PumpOutput),
   * folded into this list so the card can say whether anything is being DOSED
   * alongside whether water is moving. Its state comes from the `fert` entity's
   * phase, so `null` means "no dosing configured, nothing reported" — never off.
   */
  role: 'main_pump' | 'fertilizer' | 'aux' | 'submersible' | 'transfer' | 'generic' | 'injector';
  channel: number | string;
  relay_status: 'on' | 'off' | null;
  /** The entity's own name, when it has one worth showing (injection pumps). */
  name?: string | null;
  dev_eui: string | null;
}

export interface RunPumpState {
  pump_station_id: number;
  name: string;
  outputs: RunPumpOutputState[];
  // Verified pump-start outcome: null = pump-follow hasn't run for this station,
  // true = confirmed via uplink, false = pump_activation_failed alarm fired.
  start_confirmed?: boolean | null;
  start_detail?: string | null;
}

export interface RunValveStatesResponse {
  run_id: number;
  valves: RunValveState[];
  pumps?: RunPumpState[];
}

export const getRunValveStates = async (runId: number): Promise<RunValveStatesResponse> => {
  return apiClient.get<RunValveStatesResponse>(
    `/irrigation-programs/runs/${runId}/valve-states`,
  );
};

export const getRunDetail = async (runId: number): Promise<ExecutionRun> => {
  return apiClient.get<ExecutionRun>(`/irrigation-programs/runs/${runId}`);
};

export const abortExecution = async (runId: number): Promise<ExecutionRun> => {
  return apiClient.post<ExecutionRun>(`/irrigation-programs/runs/${runId}/abort`);
};

export const getActiveExecutions = async (farmId: string): Promise<ActiveExecutionsResponse> => {
  return apiClient.get<ActiveExecutionsResponse>(
    `/irrigation-programs/farms/${farmId}/active-executions`
  );
};

// ============================================================================
// Rule Sync API
// ============================================================================

export const syncProgramRules = async (
  programId: number,
  opts: { scheduleChanged?: boolean } = {},
): Promise<RuleSyncResponse> => {
  return apiClient.post<RuleSyncResponse>(
    `/irrigation-programs/${programId}/sync-rules`
      + (opts.scheduleChanged ? '?schedule_changed=true' : '')
  );
};

export const syncProgramValve = async (
  programId: number,
  deviceId: number,
): Promise<RuleSyncResponse> => {
  return apiClient.post<RuleSyncResponse>(
    `/irrigation-programs/${programId}/sync-rules/${deviceId}`
  );
};

export const getProgramRuleSlots = async (programId: number): Promise<RuleSlotsResponse> => {
  return apiClient.get<RuleSlotsResponse>(
    `/irrigation-programs/${programId}/rule-slots`
  );
};

export const getProgramSyncStatus = async (
  programId: number,
): Promise<ProgramSyncStatus> => {
  return apiClient.get<ProgramSyncStatus>(
    `/irrigation-programs/${programId}/sync-status`,
  );
};

// ============================================================================
// Adjustment API
// ============================================================================

export interface AdjustmentResponse {
  id: number;
  program_id: number;
  step_id: number;
  target_date: string;
  base_duration_minutes: number;
  recommended_duration_minutes: number;
  approved_duration_minutes: number | null;
  skip_step: boolean;
  skip_program: boolean;
  avg_depletion_mm: number | null;
  avg_depletion_percent: number | null;
  irrigation_need_mm: number | null;
  recommended_water_mm: number | null;
  adjustment_reason: string | null;
  adjustment_factors: Record<string, unknown> | null;
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'expired';
  generated_at: string;
  approved_at: string | null;
  approved_by_user_id: number | null;
  zone_id: number | null;
  zone_name: string | null;
  change_percent: number | null;
}

export interface AdjustmentListResponse {
  program_id: number;
  target_date: string;
  adjustments: AdjustmentResponse[];
  all_approved: boolean;
  skip_program: boolean;
}

export const generateAdjustments = async (
  programId: number,
  date?: string
): Promise<AdjustmentListResponse> => {
  const qs = date ? `?date=${date}` : '';
  return apiClient.post<AdjustmentListResponse>(
    `/irrigation-programs/${programId}/adjustments/generate${qs}`
  );
};

export const getAdjustments = async (
  programId: number,
  date?: string
): Promise<AdjustmentListResponse> => {
  const qs = date ? `?date=${date}` : '';
  return apiClient.get<AdjustmentListResponse>(
    `/irrigation-programs/${programId}/adjustments${qs}`
  );
};

export const previewAdjustments = async (
  programId: number,
  date?: string
): Promise<AdjustmentListResponse> => {
  const qs = date ? `?date=${date}` : '';
  return apiClient.get<AdjustmentListResponse>(
    `/irrigation-programs/${programId}/adjustments/preview${qs}`
  );
};

export const approveAdjustment = async (
  adjustmentId: number,
  overrideDuration?: number
): Promise<AdjustmentResponse> => {
  const body = overrideDuration != null ? { override_duration_minutes: overrideDuration } : undefined;
  return apiClient.patch<AdjustmentResponse>(
    `/irrigation-programs/adjustments/${adjustmentId}/approve`,
    body
  );
};

export const rejectAdjustment = async (
  adjustmentId: number
): Promise<AdjustmentResponse> => {
  return apiClient.patch<AdjustmentResponse>(
    `/irrigation-programs/adjustments/${adjustmentId}/reject`
  );
};

// ============================================================================
// Weekly Grid Types & API
// ============================================================================

export interface WeeklyGridAdjustment {
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'expired';
  recommended_duration_minutes: number;
  skip_step: boolean;
  avg_depletion_percent: number | null;
  adjustment_reason: string | null;
}

export interface WeeklyGridDayEntry {
  date: string;
  program_id: number;
  program_name: string;
  start_time: string;
  base_duration_minutes: number;
  effective_duration_minutes: number;
  planned_water_mm: number | null;
  effective_water_mm: number | null;
  has_fertigation: boolean;
  adjustment_mode: 'manual' | 'suggest' | 'auto';
  adjustment: WeeklyGridAdjustment | null;
}

export interface WeeklyGridSector {
  sector_id: number;
  sector_name: string;
  days: WeeklyGridDayEntry[];
}

export interface WeeklyGridZone {
  zone_id: number;
  zone_name: string;
  sectors: WeeklyGridSector[];
}

export interface WeeklyGridSummary {
  total_hours: number;
  total_programs: number;
  pending_adjustments: number;
}

export interface WeeklyGridResponse {
  week_start: string;
  week_end: string;
  zones: WeeklyGridZone[];
  summary: WeeklyGridSummary;
}

export const getWeeklyGrid = async (
  farmId: string,
  weekStart: string,
): Promise<WeeklyGridResponse> => {
  return apiClient.get<WeeklyGridResponse>(
    `/irrigation-programs/farm/${farmId}/weekly-grid?week_start=${weekStart}`
  );
};

export const approveAllAdjustments = async (
  programId: number,
  date?: string
): Promise<AdjustmentListResponse> => {
  const qs = date ? `?date=${date}` : '';
  return apiClient.post<AdjustmentListResponse>(
    `/irrigation-programs/${programId}/adjustments/approve-all${qs}`
  );
};

// ============================================================================
// Default Export
// ============================================================================

const programApi = {
  listPrograms,
  getProgram,
  createProgram,
  updateProgram,
  deleteProgram,
  updateProgramStatus,
  addStep,
  updateStep,
  deleteStep,
  reorderSteps,
  getDashboardSummary,
  confirmExecution,
  triggerExecution,
  getManualRunPreview,
  getRunHistory,
  getRunDetail,
  getRunValveStates,
  abortExecution,
  getActiveExecutions,
  syncProgramRules,
  syncProgramValve,
  getProgramRuleSlots,
  getProgramSyncStatus,
  generateAdjustments,
  getAdjustments,
  previewAdjustments,
  approveAdjustment,
  rejectAdjustment,
  approveAllAdjustments,
  getWeeklyGrid,
};

export default programApi;
